import type { GridOptions } from "ag-grid-community"
import { AgGridReact } from "ag-grid-react"
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react"

import { GRID_DEFAULT_OPTIONS, PRICING_GRID_THEME, getPricingEditColumns } from "./gridConfigs"
import type { PricingGridRow } from "./types"

import { SurchargeFormDialog } from "@/screens/Pricing/components/dialogs/SurchargeFormDialog/SurchargeFormDialog"
import { usePricingTiersActions } from "@/screens/Pricing/hooks/usePricingTiersActions"

export interface PricingTiersTableHandle {
  getAllRowData: () => PricingGridRow[]
}

interface PricingTiersTableProps {
  uomName: string
  initialRows: PricingGridRow[]
}

export const PricingTiersTable = forwardRef<PricingTiersTableHandle, PricingTiersTableProps>(
  ({ uomName, initialRows }, ref) => {
    const {
      rowData,
      modifiedRowsRef,
      editingSurchargeRowIndex,
      handleGridReady,
      handleCellValueChanged,
      handleAddPriceTier,
      handleClearPriceTier,
      handleEditSurcharge,
      handleSurchargeDialogClose,
      handleSurchargeSubmit,
      getAllRowData,
    } = usePricingTiersActions(initialRows)

    useImperativeHandle(ref, () => ({ getAllRowData }))

    const groupIndexByIdRef = useRef<Map<string, number>>(new Map())

    useMemo(() => {
      const map = new Map<string, number>()
      let nextIndex = 0
      for (const row of rowData) {
        const id = row.item.priceChangeItemId
        if (!map.has(id)) {
          map.set(id, nextIndex++)
        }
      }
      groupIndexByIdRef.current = map
    }, [rowData])

    const columnDefs = useMemo(
      () =>
        getPricingEditColumns({
          uomName,
          onAddPriceTier: handleAddPriceTier,
          onClearPriceTier: handleClearPriceTier,
          onEditSurcharge: handleEditSurcharge,
          getGroupIndex: (id) => groupIndexByIdRef.current.get(id) ?? 0,
        }),
      [uomName, handleAddPriceTier, handleClearPriceTier, handleEditSurcharge]
    )

    const gridOptions: GridOptions<PricingGridRow> = {
      rowData,
      columnDefs,
      ...GRID_DEFAULT_OPTIONS,
      rowClassRules: {
        "font-bold": ({ rowIndex }) => modifiedRowsRef.current.has(rowIndex ?? -1),
        "pricing-row-modified": ({ rowIndex }) => modifiedRowsRef.current.has(rowIndex ?? -1),
        "!bg-gray-100": ({ data }) => {
          if (!data) return false
          const groupIdx = groupIndexByIdRef.current.get(data.item.priceChangeItemId) ?? 0
          return groupIdx % 2 === 1
        },
      },
    }

    const editingRow = editingSurchargeRowIndex !== null ? rowData[editingSurchargeRowIndex] : undefined

    return (
      <>
        <AgGridReact
          {...gridOptions}
          theme={PRICING_GRID_THEME}
          onCellValueChanged={handleCellValueChanged}
          onGridReady={handleGridReady}
        />
        {editingRow && (
          <SurchargeFormDialog row={editingRow} onSubmit={handleSurchargeSubmit} onClose={handleSurchargeDialogClose} />
        )}
      </>
    )
  }
)

PricingTiersTable.displayName = "PricingTiersTable"
